import React, { useState, useEffect } from 'react';
import './Support.css';
import CustomSelectDropdown from '../../components/shared/DropDown/CustomSelectDropdown';
import axiosPrivate from 'config/axiosPrivate';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import { useTranslation } from 'react-i18next';
import usePageTracking from 'utils/hooks/usePageTracking';

const ADMIN_CLIENT_SUPPORT_PAGE = '/AdminClient/Support';

const Support = () => {
    usePageTracking(ADMIN_CLIENT_SUPPORT_PAGE);

    const [formData, setFormData] = useState({
        subject: '',
        message: '',
        email: '',
    });

    const { t } = useTranslation();
    const [charCount, setCharCount] = useState(0);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);

    const MAX_CHARS = 5000;

    useEffect(() => {
        document.title = "Typix - Support";
    }, []);

    useEffect(() => {
        const rawUser = localStorage.getItem('user');
        if (!rawUser) return;

        try {
            const userData = JSON.parse(rawUser);
            const emailFromStorage =
                userData?.user?.mail ||
                userData?.user?.email ||
                userData?.email ||
                '';

            setFormData((prev) => ({
                ...prev,
                email: emailFromStorage,
            }));
        } catch (error) {

            console.error(
                'Erreur lors de la récupération des données utilisateur:',
                error
            );
        }
    }, []);

    const handleSubjectChange = (selectedSubject) => {
        setFormData((prev) => ({
            ...prev,
            subject: selectedSubject,
        }));
        if (errors.subject) {
            setErrors((prev) => ({ ...prev, subject: '' }));
        }
    };

    const handleMessageChange = (e) => {
        const value = e.target.value;
        if (value.length <= MAX_CHARS) {
            setFormData((prev) => ({
                ...prev,
                message: value,
            }));
            setCharCount(value.length);
            if (errors.message) {
                setErrors((prev) => ({ ...prev, message: '' }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.subject) {
            newErrors.subject = t('Veuillez sélectionner un sujet');
        }

        if (!formData.message.trim()) {
            newErrors.message = t('Veuillez saisir un message');
        } else if (formData.message.trim().length < 10) {
            newErrors.message = t('Le message doit contenir au moins 10 caractères');
        }

        if (!formData.email) {
            newErrors.email = 'Email non trouvé';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setErrors((prev) => ({ ...prev, submit: '' }));

        try {
            await axiosPrivate.post(
                '/user/support',
                {
                    subject: `${formData.subject}-${formData.email}`,
                    message: formData.message,
                }
            );

            setFormData((prev) => ({
                subject: '',
                message: '',
                email: prev.email,
            }));
            setCharCount(0);
            setSubmitSuccess(true);

            setSuccessModalOpen(true);
        } catch (error) {
            console.error('Erreur lors de la soumission:', error);

            const apiMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                "Une erreur est survenue. Veuillez réessayer.";

            setErrors((prev) => ({
                ...prev,
                submit: apiMessage,
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="content-center support-page">
            <div className="support-container">
                <div className="support-card">
                    <div className="support-header">
                        <div className="support-title">{t('Nous contacter')}</div>
                        <p className="support-subtitle">
                            {t('Vous avez une question ou un problème ? Contactez-nous et nous vous répondrons dans les plus brefs délais.')}
                        </p>
                    </div>
                    <form onSubmit={handleSubmit} >
                        <div className="support-form" >
                            {/* Sujet */}
                            <div className="support-field">
                                <label htmlFor="subject" className="support-label">
                                    <img
                                        src="/Images/megaphone-02.png"
                                        alt="Icône mégaphone"
                                        className="support-label-icon"
                                    />

                                    {t('Sujet')}<span className="support-label-required">*</span>
                                </label>

                                <CustomSelectDropdown
                                    options={[t('Bug'), t('Question'), t('Feedback'), t('Demande devis'), t('Autre')]}
                                    value={formData.subject}
                                    onChange={handleSubjectChange}
                                    placeholder={t("Sélectionnez un sujet")}
                                    className="support-select"
                                    inputStyle={{
                                        width: '100%',
                                        backgroundColor: '#FFFFFF',
                                        border: '1px solid #CFCFCF',
                                        borderTop: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        ...(errors.subject ? { borderColor: '#dc2626' } : {}),
                                    }}

                                />
                                {errors.subject && (
                                    <p className="support-error-text">{errors.subject}</p>
                                )}
                            </div>

                            {/* Message */}
                            <div className="support-field" >
                                <div className="support-field-message-group">

                                    <label htmlFor="message" className="support-label--message">
                                        {t('Message')} <span className="support-label-required">*</span>
                                    </label>
                                    <textarea
                                        id="message"
                                        value={formData.message}
                                        onChange={handleMessageChange}
                                        rows="8"
                                        placeholder={t("Décrivez votre demande en détail...")}
                                        className={`support-textarea ${errors.message ? 'support-textarea--error' : ''
                                            }`}
                                    />
                                    <div className="support-field-footer">
                                        <div>
                                            {errors.message && (
                                                <p className="support-error-text">{errors.message}</p>
                                            )}
                                        </div>
                                        <p
                                            className={`support-char-counter ${charCount > MAX_CHARS * 0.9
                                                ? 'support-char-counter--warning'
                                                : ''
                                                }`}
                                        >
                                            {charCount} / {MAX_CHARS}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {/* Email */}
                            <div className="support-field">
                                <div className="support-field-message-group">
                                    <label htmlFor="email" className="support-label--tonalite">
                                        {t('Email')}
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={formData.email}
                                        readOnly
                                        className="support-input support-input--readonly"
                                    />
                                    {errors.email && (
                                        <p className="support-error-text">{errors.email}</p>
                                    )}
                                </div>

                                {/* Erreur générale */}
                                {errors.submit && (
                                    <div className="support-alert support-alert--error">
                                        <p>{errors.submit}</p>
                                    </div>
                                )}

                            </div>
                        </div>
                        <div className="support-submit-row">

                            <button
                                type="submit"
                                disabled={isSubmitting || !formData.subject || !formData.message.trim()}
                                className={`support-submit-btn ${isSubmitting ? 'support-submit-btn--loading' : ''
                                    }`}
                            >
                                {isSubmitting ? (
                                    <span className="support-submit-content">
                                        <span className="support-spinner" />
                                        {t('Envoi en cours...')}
                                    </span>
                                ) : (
                                    t('Soumettre ma demande')
                                )}
                            </button>
                        </div> </form>
                    <ProjectCreatedModal
                        isOpen={successModalOpen}
                        onClose={() => setSuccessModalOpen(false)}
                        onConfirm={() => setSuccessModalOpen(false)}
                        isError={false}
                        title="Support"
                        message={
                            <>{t('Votre demande a bien été transmise à notre équipe support, qui vous répondra dans les plus brefs délais.')}</>
                        }
                        confirmButtonText={t("Fermer")}
                    />
                </div>
            </div>
        </div>
    );
};

export default Support;


